import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Tag, Button, Space, Popconfirm, message, Switch } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import Api from '../../Network';
import SalesRepresentativeModal from './SalesRepresentativeModal';
import NameByLang from '../../Component/nameByLang';
import { usePermissions } from '../../contexts/PermissionContext';
import { AnimatedContainer, PageHeader } from '../../Component/ui';
import './style.scss';

const SalesRepresentatives = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const history = useHistory();
  const [selectedSalesRep, setSelectedSalesRep] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [visiblePasswords, setVisiblePasswords] = useState<Set<number>>(new Set());

  // Delete sales representative mutation
  const deleteSalesRepMutation = useMutation({
    mutationFn: (salesRepId: number) => Api.delete(`api/admin/drivers/${salesRepId}`),
    onSuccess: () => {
      message.success(
        t('salesRepresentatives.deleteSuccess', 'Sales representative deleted successfully')
      );
      queryClient.invalidateQueries({ queryKey: ['salesRepresentatives'] });
    },
    onError: (_error: any) => {
      message.error(t('common.error', 'Error deleting sales representative'));
    },
  });

  // Update driver status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      driverId,
      newStatus,
    }: {
      driverId: number;
      newStatus: 'Active' | 'Inactive';
    }) => {
      const response = await Api.put(`api/admin/drivers/${driverId}/status`, {
        status: newStatus,
      });
      return response.data;
    },
    onSuccess: () => {
      message.success(
        t('salesRepresentatives.statusUpdated', 'Driver status updated successfully')
      );
      queryClient.invalidateQueries({ queryKey: ['salesRepresentatives'] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        t('salesRepresentatives.updateError', 'Failed to update driver status');
      message.error(errorMessage);
    },
  });

  const handleDelete = (salesRepId: number) => {
    deleteSalesRepMutation.mutate(salesRepId);
  };

  const handleStatusChange = (driverId: number, currentStatus: string, checked: boolean) => {
    const newStatus = checked ? 'Active' : 'Inactive';
    updateStatusMutation.mutate({ driverId, newStatus });
  };

  const handleView = (record: any) => {
    history.push(`/sales-representatives/${record.id}`);
  };

  const handleEdit = (record: any) => {
    setSelectedSalesRep(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedSalesRep(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedSalesRep(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['salesRepresentatives'] });
  };

  const togglePasswordVisibility = (salesRepId: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setVisiblePasswords((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(salesRepId)) {
        newSet.delete(salesRepId);
      } else {
        newSet.add(salesRepId);
      }
      return newSet;
    });
  };

  return (
    <AnimatedContainer className="sales-representatives-page" variant="fadeUp">
      <PageHeader
        icon={<TeamOutlined />}
        title={t('salesRepresentatives.title', 'Sales Representatives')}
        subtitle={t('salesRepresentatives.subtitle', 'Manage field sales team and driver accounts')}
      />
      <Table
        tableQueryKey="salesRepresentatives"
        showSearch={true}
        hideSearch={true}
        showDeleteSwitch={false}
        hideFilters={true}
        rowKey="id"
        onRowClick={(record: any) => history.push(`/sales-representatives/${record.id}`)}
        tableFilter={{
          filters: {},
          paging: { page: 1, pageSize: 20 },
          sorting: {},
        }}
        onTableFilterChange={(_path: string, _value: any) => {
          // Handle table filter changes if needed
        }}
        columns={[
          {
            title: t('salesRepresentatives.columns.name', 'Name'),
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <NameByLang name={name} className="bold primary" />,
          },
          {
            title: t('salesRepresentatives.columns.phoneNumber', 'Phone Number'),
            dataIndex: 'phone_number',
            key: 'phone_number',
            render: (phoneNumber: string) => (
              <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>{phoneNumber || '-'}</div>
            ),
          },
          {
            title: t('salesRepresentatives.columns.password', 'Password'),
            dataIndex: 'password',
            key: 'password',
            render: (password: string, record: any) => {
              const isVisible = visiblePasswords.has(record.id);
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <span>{password ? (isVisible ? password : '••••••••') : '-'}</span>
                  {password && (
                    <Button
                      type="text"
                      size="small"
                      icon={isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={(e) => togglePasswordVisibility(record.id, e)}
                      style={{
                        padding: '4px 8px',
                        height: 'auto',
                        color: '#1890ff',
                        fontSize: '12px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        minWidth: 'auto',
                      }}
                      title={isVisible ? t('common.hide', 'Hide') : t('common.show', 'Show')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#40a9ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#1890ff';
                      }}
                    />
                  )}
                </div>
              );
            },
          },
          {
            title: t('salesRepresentatives.columns.inventory', 'Inventory'),
            dataIndex: 'inventories',
            key: 'inventories',
            render: (inventories: any) => {
              // Handle the case where inventories is an array
              if (Array.isArray(inventories) && inventories.length > 0) {
                const firstInventory = inventories[0];
                return (
                  <Tag color="blue">
                    <NameByLang
                      name_en={firstInventory?.name}
                      name_ar={firstInventory?.ar_name}
                      fallback={t('common.noData', 'No Data')}
                    />
                  </Tag>
                );
              }

              // Handle the case where inventories is a single object
              if (inventories && typeof inventories === 'object') {
                return (
                  <Tag color="blue">
                    <NameByLang
                      name_en={inventories?.name}
                      name_ar={inventories?.ar_name}
                      fallback={t('common.noData', 'No Data')}
                    />
                  </Tag>
                );
              }

              // No inventory data
              return <Tag color="default">{t('common.noData', 'No Data')}</Tag>;
            },
          },
          {
            title: t('salesRepresentatives.columns.status', 'Status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status: string, record: any) => {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag color={status === 'Active' ? 'green' : 'red'}>{status || 'Inactive'}</Tag>
                  {(hasPermission('update_sales_representative') ||
                    hasPermission('sales_representatives.update')) && (
                    <Popconfirm
                      title={
                        status === 'Active'
                          ? t(
                              'salesRepresentatives.deactivateConfirm',
                              'Are you sure you want to deactivate this driver?'
                            )
                          : t(
                              'salesRepresentatives.activateConfirm',
                              'Are you sure you want to activate this driver?'
                            )
                      }
                      onConfirm={() => handleStatusChange(record.id, status, status !== 'Active')}
                      okText={t('common.yes', 'Yes')}
                      cancelText={t('common.no', 'No')}
                      disabled={updateStatusMutation.isPending}
                    >
                      <Switch
                        checked={status === 'Active'}
                        checkedChildren={t('common.active', 'Active')}
                        unCheckedChildren={t('common.inactive', 'Inactive')}
                        loading={updateStatusMutation.isPending}
                        size="small"
                      />
                    </Popconfirm>
                  )}
                </div>
              );
            },
          },
          {
            title: t('salesRepresentatives.columns.lastUpdated', 'Last Updated'),
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (updatedAt: string) => {
              if (!updatedAt) {
                return (
                  <div style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>
                    {t('common.noData', 'No Data')}
                  </div>
                );
              }

              const date = new Date(updatedAt);
              const now = new Date();
              const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

              let timeDisplay = '';
              if (diffInHours < 1) {
                timeDisplay = t('common.justNow', 'Just now');
              } else if (diffInHours < 24) {
                timeDisplay = t('common.hoursAgo', '{{hours}} hours ago', { hours: diffInHours });
              } else {
                const diffInDays = Math.floor(diffInHours / 24);
                timeDisplay = t('common.daysAgo', '{{days}} days ago', { days: diffInDays });
              }

              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                  }}
                >
                  <ClockCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                  <div>
                    <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>
                      {date.toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                      {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{ fontSize: '11px', color: '#1890ff' }}>{timeDisplay}</div>
                  </div>
                </div>
              );
            },
          },
          {
            title: t('salesRepresentatives.columns.actions', 'Actions'),
            key: 'actions',
            width: 150,
            render: (_: any, record: any) => {
              const canView =
                hasPermission('view_sales_representatives') ||
                hasPermission('view_sales_representative') ||
                hasPermission('sales_representatives.read') ||
                hasPermission('sales_representatives.view');
              if (!canView) return null;
              return (
                <Space size="small">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    style={{ color: '#1890ff' }}
                    title={t('common.view', 'View')}
                    onClick={() => handleView(record)}
                  />
                  {(hasPermission('update_sales_representative') ||
                    hasPermission('sales_representatives.update')) && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      size="small"
                      style={{ color: '#52c41a' }}
                      title={t('common.edit', 'Edit')}
                      onClick={() => handleEdit(record)}
                    />
                  )}
                  {(hasPermission('delete_sales_representative') ||
                    hasPermission('sales_representatives.delete')) && (
                    <Popconfirm
                      title={t(
                        'salesRepresentatives.messages.deleteConfirm',
                        'Are you sure you want to delete this sales representative?'
                      )}
                      description={t(
                        'salesRepresentatives.messages.deleteWarning',
                        'This action cannot be undone.'
                      )}
                      onConfirm={() => handleDelete(record.id)}
                      okText={t('common.yes', 'Yes')}
                      cancelText={t('common.no', 'No')}
                      okType="danger"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        title={t('common.delete', 'Delete')}
                        loading={deleteSalesRepMutation.isPending}
                      />
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ]}
        loading={false}
        onClickNewButton={
          (hasPermission('create_sales_representative') ||
            hasPermission('sales_representatives.create')) &&
          handleAddNew
        }
        buttonContent={
          (hasPermission('create_sales_representative') ||
            hasPermission('sales_representatives.create')) && (
            <Button type="primary" icon={<PlusOutlined />}>
              {t('salesRepresentatives.addNew', 'Add New Sales Representative')}
            </Button>
          )
        }
      />

      {/* Sales Representative Modal */}
      <SalesRepresentativeModal
        visible={modalVisible}
        mode={modalMode}
        salesRepresentative={selectedSalesRep}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default SalesRepresentatives;
